import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import RiderDriverExclusionsApi from '../lib/api/RiderDriverExclusionsApi';
import { buildExclusionMatchSet } from '../helpers/utils/riderDriverExclusions';

/**
 * The full set of ACTIVE rider-driver exclusion pairs, for client-side
 * matching against trip driver/rider ids (HITCH-5300).
 *
 * Trip data itself stays live via Pusher, but the pair list doesn't — a pair
 * added or removed after this loads won't flag/unflag until a refetch. A
 * short staleTime plus react-query's default `refetchOnWindowFocus` refreshes
 * it whenever an admin returns to the tab, without polling continuously.
 * Shared across the Trips list and trip-detail views via one cache entry.
 *
 * `isError` MUST be surfaced by callers: a failed fetch resolves `data` to
 * undefined the same way an empty registry would, and this is a
 * safety-relevant flag — silently rendering every trip as "not excluded"
 * because the request failed is worse than showing nothing at all.
 */
const useActiveExclusionMatchSet = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['riderDriverExclusions', 'active'],
    queryFn: () => RiderDriverExclusionsApi.getAllActiveExclusions(),
    staleTime: 30_000,
  });

  const matchSet = useMemo(() => buildExclusionMatchSet(data ?? []), [data]);

  return { matchSet, isLoading, isError };
};

export default useActiveExclusionMatchSet;
